import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'

const GROUPS = ['A','B','C','D','E','F','G','H','I','J','K','L']

function calculateStandings(teams, predictions) {
  const stats = {}
  teams.forEach(t => {
    stats[t.id] = { team: t, played: 0, won: 0, drawn: 0, lost: 0, gf: 0, ga: 0, points: 0 }
  })
  predictions.forEach(({ homeTeam, awayTeam, homeGoals, awayGoals }) => {
    if (homeGoals === '' || awayGoals === '' || homeGoals === undefined || awayGoals === undefined) return
    const h = parseInt(homeGoals)
    const a = parseInt(awayGoals)
    if (isNaN(h) || isNaN(a)) return
    stats[homeTeam.id].played++
    stats[awayTeam.id].played++
    stats[homeTeam.id].gf += h
    stats[homeTeam.id].ga += a
    stats[awayTeam.id].gf += a
    stats[awayTeam.id].ga += h
    if (h > a) { stats[homeTeam.id].won++; stats[homeTeam.id].points += 3; stats[awayTeam.id].lost++ }
    else if (h < a) { stats[awayTeam.id].won++; stats[awayTeam.id].points += 3; stats[homeTeam.id].lost++ }
    else { stats[homeTeam.id].drawn++; stats[homeTeam.id].points++; stats[awayTeam.id].drawn++; stats[awayTeam.id].points++ }
  })
  return Object.values(stats).sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points
    const gdA = a.gf - a.ga, gdB = b.gf - b.ga
    if (gdB !== gdA) return gdB - gdA
    return b.gf - a.gf
  })
}

export default function Entry() {
  const { entryId } = useParams()
  const [entry, setEntry] = useState(null)
  const [teams, setTeams] = useState({})
  const [matches, setMatches] = useState({})
  const [predictions, setPredictions] = useState({})
  const [bonuses, setBonuses] = useState({ golden_boot: '', golden_gloves: '', player_tournament: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [locked, setLocked] = useState(false) // eslint-disable-line no-unused-vars
  const [error, setError] = useState(null)
  const [activeGroup, setActiveGroup] = useState('A')
  const [activeSection, setActiveSection] = useState('groups')

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) { window.location.href = '/login'; return }
      const { data: entryData, error: entryError } = await supabase.from('entries').select('*').eq('id', entryId).eq('user_id', user.id).single()
      if (entryError || !entryData) { setError('Entry not found.'); setLoading(false); return }
      setEntry(entryData)
      const { data: settingsData } = await supabase.from('settings').select('value').eq('key', 'phase1_lock_date').single()
      const pastLockDate = settingsData && new Date() > new Date(settingsData.value)
      if (pastLockDate && entryData.phase1_locked) setLocked(true)
      const { data: teamsData } = await supabase.from('teams').select('*').order('group_code')
      const grouped = {}
      teamsData.forEach(t => { if (!grouped[t.group_code]) grouped[t.group_code] = []; grouped[t.group_code].push(t) })
      setTeams(grouped)
      const { data: matchesData } = await supabase.from('matches').select('*, home:home_team_id(id,name,flag_emoji), away:away_team_id(id,name,flag_emoji)').eq('phase', 'group').order('match_number')
      const groupedMatches = {}
      matchesData.forEach(m => { if (!groupedMatches[m.group_code]) groupedMatches[m.group_code] = []; groupedMatches[m.group_code].push(m) })
      setMatches(groupedMatches)
      const { data: existingPreds } = await supabase.from('match_predictions').select('*').eq('entry_id', entryId)
      const predMap = {}
      existingPreds?.forEach(p => { predMap[p.match_id] = { home: p.predicted_home_goals, away: p.predicted_away_goals } })
      setPredictions(predMap)
      const { data: existingBonus } = await supabase.from('bonus_predictions').select('*').eq('entry_id', entryId)
      const bonusMap = { golden_boot: '', golden_gloves: '', player_tournament: '' }
      existingBonus?.forEach(b => { bonusMap[b.category] = b.predicted_player_name || '' })
      setBonuses(bonusMap)
      setLoading(false)
    }
    load()
  }, [entryId])

  function setScore(matchId, side, value) {
    if (locked) return
    setPredictions(prev => ({ ...prev, [matchId]: { ...prev[matchId], [side]: value === '' ? '' : parseInt(value) || 0 } }))
  }

  function getStandings(group) {
    const groupTeams = teams[group] || []
    const groupMatches = matches[group] || []
    const predList = groupMatches.map(m => ({ homeTeam: m.home, awayTeam: m.away, homeGoals: predictions[m.id]?.home, awayGoals: predictions[m.id]?.away }))
    return calculateStandings(groupTeams, predList)
  }

  async function handleSave() {
    if (locked) return
    setSaving(true)
    setSaved(false)
    const matchPreds = []
    Object.entries(predictions).forEach(([matchId, val]) => {
      if (val.home !== '' && val.away !== '' && val.home !== undefined && val.away !== undefined) {
        matchPreds.push({ entry_id: entryId, match_id: parseInt(matchId), predicted_home_goals: val.home, predicted_away_goals: val.away, points_awarded: 0 })
      }
    })
    const bonusPreds = [
      { entry_id: entryId, category: 'golden_boot', predicted_player_name: bonuses.golden_boot },
      { entry_id: entryId, category: 'golden_gloves', predicted_player_name: bonuses.golden_gloves },
      { entry_id: entryId, category: 'player_tournament', predicted_player_name: bonuses.player_tournament }
    ].filter(b => b.predicted_player_name)
    try {
      if (matchPreds.length > 0) await supabase.from('match_predictions').upsert(matchPreds, { onConflict: 'entry_id,match_id' })
      if (bonusPreds.length > 0) await supabase.from('bonus_predictions').upsert(bonusPreds, { onConflict: 'entry_id,category' })
      setSaved(true)
    } catch (err) { setError('Something went wrong. Please try again.') }
    setSaving(false)
  }

  if (loading) return <div><nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav><div className="container"><p className="text-muted">Loading...</p></div></div>
  if (error) return <div><nav className="topnav"><a href="/" className="topnav-logo">PREDICTA</a></nav><div className="container"><div className="card"><p style={{ color: 'var(--red)' }}>{error}</p></div></div></div>

  const standings = getStandings(activeGroup)

  return (
    <div>
      <nav className="topnav">
        <a href="/" className="topnav-logo">PREDICTA</a>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <a href="/world-cup" className="btn btn-secondary" style={{ padding: '7px 14px', fontSize: '13px' }}>Dashboard</a>
          {!locked && <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '7px 14px', fontSize: '13px' }}>{saving ? 'Saving...' : 'Save'}</button>}
          {locked && <span style={{ fontSize: '13px', color: 'var(--red)' }}>Predictions locked</span>}
        </div>
      </nav>

      <div className="container">
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'var(--ff-d)', fontWeight: '700', fontSize: '28px', marginBottom: '4px' }}>{entry.entry_name}</h1>
          <p style={{ color: 'var(--text-2)' }}>Group stage predictions</p>
        </div>

        {saved && (
          <div style={{ background: 'var(--gold-bg)', border: '1px solid var(--gold-border)', borderRadius: 'var(--r-lg)', padding: '12px 16px', marginBottom: '16px' }}>
            <p style={{ color: 'var(--gold)', fontSize: '13px', fontWeight: '500' }}>Predictions saved successfully.</p>
          </div>
        )}

        <div className="tab-bar">
          <button className={`tab-btn ${activeSection === 'groups' ? 'active' : ''}`} onClick={() => setActiveSection('groups')}>Group Stage</button>
          <button className={`tab-btn ${activeSection === 'bonus' ? 'active' : ''}`} onClick={() => setActiveSection('bonus')}>Bonus Picks</button>
        </div>

        {activeSection === 'groups' && (
          <>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {GROUPS.map(g => (
                <button key={g} onClick={() => setActiveGroup(g)} style={{
                  padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: '13px', cursor: 'pointer', fontFamily: 'var(--ff-d)', fontWeight: '600', letterSpacing: '.04em',
                  background: activeGroup === g ? 'var(--gold-bg)' : 'transparent',
                  color: activeGroup === g ? 'var(--gold)' : 'var(--text-2)',
                  border: activeGroup === g ? '1px solid var(--gold-border)' : '1px solid var(--border)',
                }}>
                  Group {g}
                </button>
              ))}
            </div>

            {matches[activeGroup] && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,300px),1fr))', gap: '14px' }}>
                <div className="card" style={{ marginBottom: 0 }}>
                  <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '16px' }}>Match Predictions</h3>
                  {matches[activeGroup].map(match => {
                    const pred = predictions[match.id] || {}
                    return (
                      <div key={match.id} style={{ padding: '12px 0', borderBottom: '1px solid var(--border-sub)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'space-between' }}>
                          <span style={{ flex: 1, fontSize: '13px', fontWeight: '500' }}>{match.home.flag_emoji} {match.home.name}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <input type="number" min="0" max="20" className="score-input" value={pred.home ?? ''} disabled={locked} style={{ opacity: locked ? 0.5 : 1 }} onChange={e => setScore(match.id, 'home', e.target.value)} />
                            <span style={{ color: 'var(--text-3)', fontSize: '14px', fontWeight: '700' }}>:</span>
                            <input type="number" min="0" max="20" className="score-input" value={pred.away ?? ''} disabled={locked} style={{ opacity: locked ? 0.5 : 1 }} onChange={e => setScore(match.id, 'away', e.target.value)} />
                          </div>
                          <span style={{ flex: 1, textAlign: 'right', fontSize: '13px', fontWeight: '500' }}>{match.away.name} {match.away.flag_emoji}</span>
                        </div>
                      </div>
                    )
                  })}
                </div>

                <div className="card" style={{ marginBottom: 0 }}>
                  <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '18px', marginBottom: '16px' }}>Predicted Standings</h3>
                  <table style={{ fontSize: '12px' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '24px', padding: '6px 4px' }}>#</th>
                        <th style={{ padding: '6px 4px' }}>Team</th>
                        <th style={{ textAlign: 'center', width: '28px', padding: '6px 4px' }}>P</th>
                        <th style={{ textAlign: 'center', width: '28px', padding: '6px 4px' }}>W</th>
                        <th style={{ textAlign: 'center', width: '28px', padding: '6px 4px' }}>D</th>
                        <th style={{ textAlign: 'center', width: '28px', padding: '6px 4px' }}>L</th>
                        <th style={{ textAlign: 'center', width: '32px', padding: '6px 4px' }}>GD</th>
                        <th style={{ textAlign: 'center', width: '32px', padding: '6px 4px' }}>Pts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((s, i) => (
                        <tr key={s.team.id} style={{ background: i < 2 ? 'var(--gold-bg)' : 'transparent' }}>
                          <td style={{ padding: '8px 4px', color: i < 2 ? 'var(--gold)' : 'var(--text-3)', fontWeight: '600' }}>{i + 1}</td>
                          <td style={{ padding: '8px 4px', fontWeight: i < 2 ? '500' : '400' }}>{s.team.flag_emoji} {s.team.name}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center', color: 'var(--text-3)' }}>{s.played}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center', color: 'var(--text-3)' }}>{s.won}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center', color: 'var(--text-3)' }}>{s.drawn}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center', color: 'var(--text-3)' }}>{s.lost}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center', color: 'var(--text-3)' }}>{s.gf - s.ga}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'center', fontWeight: '700', color: i < 2 ? 'var(--gold)' : 'var(--text)' }}>{s.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '10px' }}>Gold = qualifies. Updates as you enter scores.</p>
                </div>
              </div>
            )}
          </>
        )}

        {activeSection === 'bonus' && (
          <div className="card">
            <h3 style={{ fontFamily: 'var(--ff-d)', fontSize: '20px', marginBottom: '6px' }}>Bonus Predictions</h3>
            <p style={{ color: 'var(--text-2)', fontSize: '13px', marginBottom: '20px' }}>Optional bonus picks for extra points.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {[
                ['golden_boot', 'Golden Boot Winner', 'e.g. Kylian Mbappe'],
                ['golden_gloves', 'Golden Gloves Winner', 'e.g. Emiliano Martinez'],
                ['player_tournament', 'Player of the Tournament', 'e.g. Vinicius Jr'],
              ].map(([key, label, placeholder]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--text-2)', marginBottom: '7px' }}>{label}</label>
                  <input type="text" placeholder={placeholder} value={bonuses[key]} onChange={e => setBonuses(prev => ({ ...prev, [key]: e.target.value }))} disabled={locked} />
                </div>
              ))}
            </div>
            {!locked && (
              <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ width: '100%', padding: '12px', fontSize: '14px', marginTop: '20px' }}>
                {saving ? 'Saving...' : 'Save Bonus Picks'}
              </button>
            )}
          </div>
        )}

        {!locked && activeSection === 'groups' && (
          <div style={{ textAlign: 'center', marginTop: '24px', paddingBottom: '40px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ padding: '13px 48px', fontSize: '15px' }}>
              {saving ? 'Saving...' : 'Save All Predictions'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}